import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const PrivacidadePage = () => {
  const [activeSection, setActiveSection] = useState('');

  const sections = [
    { id: 'dados-recolhidos', title: 'Dados Recolhidos' },
    { id: 'como-usamos', title: 'Como Usamos os Dados' },
    { id: 'partilha', title: 'Partilha com Terceiros' },
    { id: 'cookies', title: 'Cookies' },
    { id: 'direitos', title: 'Direitos do Titular' },
    { id: 'retencao', title: 'Retenção de Dados' },
    { id: 'contacto', title: 'Contacto do Responsável' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 mt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-2 mb-4">
              <i className="ri-shield-check-line text-3xl text-teal-400"></i>
              <span className="text-teal-400 text-sm font-semibold uppercase tracking-wider">
                Proteção de Dados
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Política de Privacidade
            </h1>
            <p className="text-xl text-slate-300 mb-6 leading-relaxed">
              A sua privacidade é importante para nós. Conheça como recolhemos, usamos e protegemos os seus dados pessoais.
            </p>
            <div className="flex items-center space-x-2 text-sm text-slate-400">
              <i className="ri-calendar-line"></i>
              <span>Última atualização: 15 de janeiro de 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Index */}
      <section className="bg-slate-50 border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 overflow-x-auto scrollbar-hide">
            <span className="text-sm font-semibold text-slate-600 whitespace-nowrap mr-2">
              Índice:
            </span>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSection === section.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Introdução */}
          <div className="mb-12 p-6 bg-teal-50 border-l-4 border-teal-600 rounded-r-lg">
            <p className="text-slate-700 leading-relaxed">
              A <strong>Seaview & Levada Escape</strong> compromete-se a proteger a privacidade dos seus hóspedes e visitantes do website. Esta Política de Privacidade explica como recolhemos, utilizamos, armazenamos e protegemos os seus dados pessoais, em conformidade com o Regulamento Geral de Proteção de Dados (RGPD).
            </p>
          </div>

          {/* Dados Recolhidos */}
          <div id="dados-recolhidos" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-file-list-3-line text-lg"></i>
              </span>
              Dados Recolhidos
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Para garantir uma experiência de reserva e estadia adequada, recolhemos os seguintes dados pessoais:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Identificação:</strong> Nome completo, data de nascimento, nacionalidade, número e tipo de documento de identificação (Cartão de Cidadão, Passaporte)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Contacto:</strong> Endereço de email, número de telefone, morada de residência</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Reserva:</strong> Datas de check-in e check-out, número de hóspedes, preferências de apartamento</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Pagamento:</strong> Informações de cartão de crédito/débito ou outros métodos de pagamento (processados por plataformas seguras de terceiros)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Navegação:</strong> Endereço IP, tipo de navegador, páginas visitadas, tempo de permanência no site</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Como Usamos os Dados */}
          <div id="como-usamos" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-settings-3-line text-lg"></i>
              </span>
              Como Usamos os Dados
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Os dados pessoais recolhidos são utilizados exclusivamente para as seguintes finalidades:
              </p>
              <div className="grid gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <i className="ri-calendar-check-line text-teal-600 mr-2"></i>
                    Gestão de Reservas
                  </h3>
                  <p className="text-slate-700 text-sm">
                    Processar e confirmar reservas, enviar confirmações e lembretes, gerir check-in e check-out.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <i className="ri-customer-service-2-line text-teal-600 mr-2"></i>
                    Comunicação com Hóspedes
                  </h3>
                  <p className="text-slate-700 text-sm">
                    Responder a pedidos de informação, fornecer assistência durante a estadia, enviar informações relevantes sobre o alojamento.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <i className="ri-bank-card-line text-teal-600 mr-2"></i>
                    Processamento de Pagamentos
                  </h3>
                  <p className="text-slate-700 text-sm">
                    Processar pagamentos de reservas, depósitos de segurança e eventuais cobranças adicionais.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <i className="ri-file-text-line text-teal-600 mr-2"></i>
                    Cumprimento de Obrigações Legais
                  </h3>
                  <p className="text-slate-700 text-sm">
                    Cumprir obrigações fiscais e de registo de hóspedes junto das autoridades competentes (SEF, Finanças).
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <i className="ri-line-chart-line text-teal-600 mr-2"></i>
                    Melhoria dos Serviços
                  </h3>
                  <p className="text-slate-700 text-sm">
                    Analisar preferências e feedback para melhorar a qualidade dos nossos apartamentos e serviços.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Partilha com Terceiros */}
          <div id="partilha" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-share-line text-lg"></i>
              </span>
              Partilha com Terceiros
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Os seus dados pessoais podem ser partilhados com terceiros apenas nas seguintes situações:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-government-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Autoridades Competentes:</strong> Partilha obrigatória de dados de hóspedes com o SEF (Serviço de Estrangeiros e Fronteiras) e Autoridade Tributária, conforme exigido por lei</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-bank-card-2-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Processadores de Pagamento:</strong> Plataformas seguras de pagamento (Stripe, PayPal, etc.) para processar transações financeiras</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-send-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Fornecedores de Serviços:</strong> Empresas que nos auxiliam no envio de emails, gestão de reservas e manutenção do website</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Proteção Legal:</strong> Quando necessário para proteger os nossos direitos legais ou cumprir ordens judiciais</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                <p className="text-sm text-slate-700">
                  <strong>Nota:</strong> Nunca vendemos, alugamos ou partilhamos os seus dados pessoais com terceiros para fins de marketing sem o seu consentimento explícito.
                </p>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div id="cookies" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-cookie-line text-lg"></i>
              </span>
              Cookies
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                O nosso website utiliza cookies para melhorar a sua experiência de navegação. Os cookies são pequenos ficheiros de texto armazenados no seu dispositivo.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">Cookies Essenciais</h3>
                  <p className="text-slate-700 text-sm">
                    Necessários para o funcionamento básico do website, como manter a sessão ativa e garantir a segurança.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">Cookies de Desempenho</h3>
                  <p className="text-slate-700 text-sm">
                    Recolhem informações sobre como os visitantes utilizam o website, permitindo-nos melhorar o seu funcionamento.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">Cookies de Funcionalidade</h3>
                  <p className="text-slate-700 text-sm">
                    Permitem que o website se lembre das suas escolhas (como idioma ou região) para proporcionar uma experiência personalizada.
                  </p>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed mt-4">
                Pode gerir ou desativar cookies nas configurações do seu navegador. No entanto, a desativação de alguns cookies pode afetar a funcionalidade do website.
              </p>
            </div>
          </div>

          {/* Direitos do Titular */}
          <div id="direitos" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-user-star-line text-lg"></i>
              </span>
              Direitos do Titular dos Dados
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                De acordo com o RGPD, tem os seguintes direitos relativamente aos seus dados pessoais:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-eye-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito de Acesso</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Solicitar uma cópia dos dados pessoais que detemos sobre si.
                  </p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-edit-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito de Retificação</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Corrigir dados pessoais incorretos ou incompletos.
                  </p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-delete-bin-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito ao Apagamento</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Solicitar a eliminação dos seus dados pessoais (sujeito a obrigações legais).
                  </p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-forbid-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito de Oposição</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Opor-se ao tratamento dos seus dados para determinadas finalidades.
                  </p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-download-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito à Portabilidade</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Receber os seus dados num formato estruturado e legível por máquina.
                  </p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center mb-2">
                    <i className="ri-pause-circle-line text-teal-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-slate-900">Direito à Limitação</h3>
                  </div>
                  <p className="text-slate-700 text-sm">
                    Solicitar a limitação do tratamento dos seus dados em certas circunstâncias.
                  </p>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed mt-6">
                Para exercer qualquer um destes direitos, entre em contacto connosco através dos meios indicados na secção "Contacto do Responsável".
              </p>
            </div>
          </div>

          {/* Retenção de Dados */}
          <div id="retencao" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-time-line text-lg"></i>
              </span>
              Retenção de Dados
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-4">
                Os seus dados pessoais são conservados apenas pelo tempo necessário para cumprir as finalidades para as quais foram recolhidos:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-calendar-check-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Reserva:</strong> Conservados durante 5 anos após a conclusão da estadia, para fins fiscais e contabilísticos</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Identificação:</strong> Conservados durante 3 anos após o check-out, conforme exigido pela legislação de registo de hóspedes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Comunicação:</strong> Conservados enquanto mantiver uma relação comercial connosco ou até solicitar a sua eliminação</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-bank-card-line text-teal-600 mt-1 mr-2"></i>
                  <span><strong>Dados de Pagamento:</strong> Não armazenamos dados completos de cartão; são processados por plataformas seguras de terceiros</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                Após o período de retenção, os dados são eliminados de forma segura ou anonimizados para fins estatísticos.
              </p>
            </div>
          </div>

          {/* Contacto do Responsável */}
          <div id="contacto" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <span className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg mr-3">
                <i className="ri-contacts-line text-lg"></i>
              </span>
              Contacto do Responsável pelo Tratamento de Dados
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-6">
                Se tiver questões sobre esta Política de Privacidade ou pretender exercer os seus direitos, pode contactar-nos através de:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-6 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center mb-3">
                    <i className="ri-mail-line text-teal-600 text-2xl mr-3"></i>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                  </div>
                  <a
                    href="mailto:info@seaviewlevada.com"
                    className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                  >
                    info@seaviewlevada.com
                  </a>
                </div>
                <div className="p-6 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center mb-3">
                    <i className="ri-whatsapp-line text-teal-600 text-2xl mr-3"></i>
                    <h3 className="font-semibold text-slate-900">WhatsApp</h3>
                  </div>
                  <a
                    href="https://wa.me/351962050526"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                  >
                    +351 962 050 526
                  </a>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                <p className="text-sm text-slate-700">
                  <strong>Autoridade de Controlo:</strong> Tem o direito de apresentar uma reclamação junto da Comissão Nacional de Proteção de Dados (CNPD) caso considere que os seus direitos não foram respeitados.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Final */}
          <div className="mt-16 p-8 bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl text-white text-center">
            <i className="ri-question-line text-5xl mb-4"></i>
            <h3 className="text-2xl font-bold mb-3">Tem Dúvidas sobre a Privacidade?</h3>
            <p className="text-teal-100 mb-6 max-w-2xl mx-auto">
              A nossa equipa está disponível para esclarecer qualquer questão relacionada com a proteção dos seus dados pessoais.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/351962050526"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-white text-teal-600 rounded-full font-semibold hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap flex items-center space-x-2"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="mailto:info@seaviewlevada.com"
                className="px-8 py-3 bg-teal-800 text-white rounded-full font-semibold hover:bg-teal-900 transition-all cursor-pointer whitespace-nowrap flex items-center space-x-2"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Enviar Email</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacidadePage;